import React, { useEffect, useState } from "react";
import { adminApi, authStore } from "../../api/adminApi";
import { agentPages } from "../../permissions";
import "../module.scss";
import "./users.scss";

const emptyForm = { name: "", email: "", role: "agent", permissions: agentPages.map(page => page.key), password: "" };
const userRows = response => Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : Array.isArray(response?.users) ? response.users : null;
const userActive = user => {
  const value = user?.is_active ?? user?.isActive;
  return value === undefined ? null : value === true || value === 1 || value === "1";
};
const sameAccount = (a, b) =>
  (a?.id != null && b?.id != null && String(a.id) === String(b.id)) ||
  (a?.email && b?.email && a.email.trim().toLowerCase() === b.email.trim().toLowerCase());

export default function UsersPage() {
  const currentUser = authStore.user();
  const site = currentUser.site?.key || "cloudkia";
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const refresh = async () => {
    const rows = userRows(await adminApi.users(site));
    if (!rows) throw new Error("The users API returned an unexpected list format.");
    setUsers(rows);
    return rows;
  };

  useEffect(() => {
    let active = true;
    adminApi.users(site).then(response => {
      if (!active) return;
      const rows = userRows(response);
      if (!rows) throw new Error("The users API returned an unexpected list format.");
      setUsers(rows);
    }).catch(err => { if (active) setError(err.message); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [site]);

  const edit = user => {
    setEditingId(user.id);
    setForm({ name: user.name || "", email: user.email || "", role: user.role || "agent", permissions: Array.isArray(user.permissions) ? user.permissions.filter(key => agentPages.some(page => page.key === key)) : agentPages.map(page => page.key), password: "" });
    setError(""); setNotice("");
  };
  const cancel = () => { setEditingId(null); setForm(emptyForm); setError(""); };

  const save = async event => {
    event.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    if (!name || !email || (!editingId && form.password.length < 12)) {
      setError("Enter a name, email, and a password of at least 12 characters for a new user."); return;
    }
    setSaving(true); setError(""); setNotice("");
    try {
      if (editingId) {
        const changes = { name, email, role: form.role, ...(form.role === "agent" ? { permissions: form.permissions } : {}) };
        if (form.password) changes.password = form.password;
        await adminApi.updateUser(site, editingId, changes);
      } else {
        await adminApi.createUser(site, { name, email, role: form.role, password: form.password, ...(form.role === "agent" ? { permissions: form.permissions } : {}) });
      }
      await refresh();
      setNotice(editingId ? "User updated." : "User created.");
      setEditingId(null); setForm(emptyForm);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return <div className="module-page users-page">
    <div className="module-page__intro"><div><h2>Team members</h2><p>Manage access to {currentUser.site?.name || site}. Admins have full access; choose each Agent's pages below.</p></div></div>
    {error && <div className="notice notice--error" role="alert">{error}</div>}
    {notice && <div className="notice notice--success" role="status">{notice}</div>}
    <div className="users-page__grid">
      <section className="module-card users-page__list"><header><div><h3>Users</h3><p>{users.length} account{users.length === 1 ? "" : "s"} for this site</p></div></header>
        {loading ? <p>Loading users…</p> : users.length ? <div className="users-page__table-wrap"><table><thead><tr><th>User</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead><tbody>{users.map(user => {
          const active = userActive(user);
          return <tr key={user.id}><td><strong>{user.name || "Unnamed user"}</strong><small>{user.email}</small></td><td>{user.role === "admin" ? "Admin" : "Agent"}<small>{user.role === "admin" ? "All pages" : `${user.permissions?.length ?? agentPages.length} page groups`}</small></td><td><span className={`users-page__status ${active ? "users-page__status--active" : ""}`}>{active === null ? "Unknown" : active ? "Active" : "Inactive"}</span></td><td><div className="users-page__actions"><button type="button" onClick={() => edit(user)} disabled={saving}>Edit</button></div></td></tr>;
        })}</tbody></table></div> : <p className="users-page__empty">No users returned for this site.</p>}
      </section>
      <form className="module-card users-page__form" onSubmit={save}><header><div><h3>{editingId ? "Edit user" : "Add user"}</h3><p>{editingId ? "Update access or set a new password." : "Create an account for a team member."}</p></div></header>
        <label>Name<input required maxLength="100" autoComplete="name" value={form.name} onChange={event => setForm(value => ({ ...value, name: event.target.value }))} /></label>
        <label>Email<input required type="email" maxLength="254" autoComplete="email" value={form.email} onChange={event => setForm(value => ({ ...value, email: event.target.value }))} /></label>
        <label>Role<select value={form.role} disabled={users.some(user => String(user.id) === String(editingId) && sameAccount(user, currentUser))} onChange={event => setForm(value => ({ ...value, role: event.target.value }))}><option value="agent">Agent</option><option value="admin">Admin</option></select></label>
        {form.role === "agent" ? <fieldset className="users-page__permissions"><legend>Pages this Agent can access</legend>{agentPages.map(page => <label key={page.key}><input type="checkbox" checked={form.permissions.includes(page.key)} onChange={event => setForm(value => ({ ...value, permissions: event.target.checked ? [...value.permissions, page.key] : value.permissions.filter(key => key !== page.key) }))} />{page.label}</label>)}<p>Chatbot Settings, Flow, AI Settings, and Users require the Admin role.</p></fieldset> : <p className="users-page__full-access">Admins can access every page and manage users.</p>}
        <label>{editingId ? "New password (optional)" : "Password"}<input type="password" minLength="12" required={!editingId} autoComplete="new-password" value={form.password} onChange={event => setForm(value => ({ ...value, password: event.target.value }))} /></label>
        <div className="users-page__form-actions"><button className="primary-button" disabled={saving}>{saving ? "Saving…" : editingId ? "Save user" : "Create user"}</button>{editingId && <button className="secondary-button" type="button" onClick={cancel} disabled={saving}>Cancel</button>}</div>
      </form>
    </div>
  </div>;
}
